import { useNavigate } from 'react-router-dom';
import { createElement, useEffect, useRef } from 'react';
import { createBridgeContext } from '@dvag/mfe-bridge';
import { getToken } from '@dvag/dfs-orchestrator-client';

export type KundenstammdatenElement = HTMLElement & {
  authenticationToken?: () => Promise<string | void>;
  kundennummer?: string;
  bankverbindungId?: string;
};

export type GetElement = KundenstammdatenElement | null;

type CreateElementProps = {
  querySelector: string;
  scriptSrc: string;
  createElementParams: {
    mfeBridgeIdentifier: string;
    kundennummer?: string;
    bankverbindungId?: string;
  };
};

export const useCreateKssElement = ({
  createElementParams,
  querySelector,
  scriptSrc,
}: CreateElementProps) => {
  const navigate = useNavigate();
  const isBridgeHandlerRegistered = useRef(false);

  useEffect(() => {
    if (isBridgeHandlerRegistered.current) {
      return;
    }

    isBridgeHandlerRegistered.current = true;

    const bridge = createBridgeContext();
    const connectorKundenstammdaten = bridge.registerChildMFE(
      createElementParams.mfeBridgeIdentifier,
    );

    const getElement = (): GetElement => document.querySelector(querySelector);

    const setLuigiAuthenticationProvider = (): void => {
      const element = getElement();
      if (!element) {
        return;
      }

      element.authenticationToken = () => Promise.resolve(getToken());
    };

    connectorKundenstammdaten.onConnection(() => setLuigiAuthenticationProvider());

    connectorKundenstammdaten.onEventFromMFE((eventName) => {
      if (eventName === 'close') {
        navigate(-1);
      }
      if (eventName === 'back') {
        navigate(-1);
      }
    });

    if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
      const script = document.createElement('script');
      script.src = scriptSrc;
      script.type = 'module';
      document.head.appendChild(script);
    }
  }, [createElementParams.mfeBridgeIdentifier, navigate, querySelector, scriptSrc]);

  return createElement(querySelector, {
    'mfe-bridge-identifier': createElementParams.mfeBridgeIdentifier,
    kundennummer: createElementParams.kundennummer,
    'bankverbindung-id': createElementParams.bankverbindungId,
  });
};
